import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Activity, PlayCircle, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Combobox } from '@/components/ui/Combobox';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { RunStatusBadge, runStatusLabel } from '@/components/ui/RunStatusBadge';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { runModeLabel } from '@/lib/format';
import { useCustomers } from '@/hooks/useCustomers';
import { useCursorPager } from '@/hooks/useCursorPager';
import { useRoutines, type Routine } from '@/hooks/useRoutines';
import {
  isActive,
  useRuns,
  type Run,
  type RunStatus,
  type RunsFilter,
} from '@/hooks/useRuns';
import { failureTone, type WithRunFailure } from '@/lib/failureReason';

/** Run + berechnete Fehlerursache (Backend liefert das Feld immer mit). */
type RunRow = WithRunFailure<Run>;

/**
 * /runs - Run-History (Sprint 5).
 *
 * Filterbar nach Customer, Status. Live-Polling alle 5s wenn aktive Runs in
 * der Liste sind (siehe useRuns refetchInterval).
 */

export const Route = createFileRoute('/runs/')({
  component: RunsPage,
});

const STATUS_OPTIONS: RunStatus[] = [
  'queued',
  'preparing',
  'running',
  'success',
  'partial',
  'failed',
  // Eigener Filter-Eintrag: "welche Laeufe haben nie stattgefunden?" ist die
  // Frage nach einer Wartungsrunde, und in 'failed' ging sie bisher unter.
  'blocked',
  'aborted',
  'rolled_back',
];

function RunsPage(): JSX.Element {
  const [filter, setFilter] = useState<RunsFilter>({});
  // Kein searchSchema auf dieser Route: Seitengröße bleibt Komponenten-State.
  const [limit, setLimit] = useState(50);
  // GET /runs kennt kein Cursor-Blättern (limit schneidet die jüngsten
  // Läufe ab). Der Pager steuert deshalb nur die Seitengröße.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const navigate = useNavigate();

  const customersQuery = useCustomers({ limit: 200 });
  const routinesQuery = useRoutines();
  const runsQuery = useRuns({ ...filter, limit });
  const pager = useCursorPager({
    cursor,
    nextCursor: null,
    onCursorChange: setCursor,
    pageSize: limit,
    onPageSizeChange: (size) => {
      setLimit(size);
      setCursor(undefined);
    },
  });

  // Namens-Auflösung: customer_id -> Kunde, routine_id -> Routine-Name.
  const customerName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of customersQuery.data?.data ?? []) m.set(c.id, c.name);
    return m;
  }, [customersQuery.data]);

  const routineNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of routinesQuery.data?.data ?? []) m.set(r.id, r.display_name);
    return m;
  }, [routinesQuery.data]);

  // routineSlug (= Routine-Code) -> routine.id, für den clientseitigen Filter,
  // weil Runs ihre Routinen als UUIDs (routine_ids) tragen.
  const routineIdByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of routinesQuery.data?.data ?? []) m.set(r.code, r.id);
    return m;
  }, [routinesQuery.data]);

  // Server filtert nur customer_id/status/limit. Routine + Ab-Datum sind
  // clientseitig (Backend kennt diese Query-Params nicht).
  const runs: RunRow[] = useMemo(() => {
    let list: RunRow[] = runsQuery.data?.data ?? [];
    if (filter.routineSlug) {
      const routineId = routineIdByCode.get(filter.routineSlug);
      list = routineId
        ? list.filter((r) => r.routine_ids.includes(routineId))
        : [];
    }
    if (filter.startedAfter) {
      const after = filter.startedAfter;
      list = list.filter((r) => {
        const started = r.started_at ?? r.created_at;
        return started != null && started >= after;
      });
    }
    return list;
  }, [runsQuery.data, filter.routineSlug, filter.startedAfter, routineIdByCode]);

  const columns = useMemo((): ReadonlyArray<Column<RunRow>> => {
    const routineLabel = (ids: string[]): string => {
      if (ids.length === 0) return '–';
      const names = ids.map((id) => routineNameById.get(id) ?? id.slice(0, 8));
      return names.join(', ');
    };
    return [
      {
        id: 'id',
        header: 'ID',
        filterValue: (run) => run.id.slice(0, 8),
        cell: (run) => <code className="font-mono text-xs">{run.id.slice(0, 8)}</code>,
      },
      {
        id: 'kunde',
        header: 'Kunde',
        filterValue: (run) => customerName.get(run.customer_id) ?? run.customer_id.slice(0, 8),
        cell: (run) => (
          <span className="text-sm">
            {customerName.get(run.customer_id) ?? run.customer_id.slice(0, 8)}
          </span>
        ),
      },
      {
        id: 'routine',
        header: 'Routine',
        filterValue: (run) => {
          const label = routineLabel(run.routine_ids);
          return label === '–' ? '' : label;
        },
        cell: (run) => <span className="text-sm">{routineLabel(run.routine_ids)}</span>,
      },
      {
        id: 'mode',
        header: 'Mode',
        filter: 'select',
        filterValue: (run) => {
          const label = runModeLabel(run.mode);
          return label === '–' ? '' : label;
        },
        cell: (run) => (
          <div className="flex flex-col items-start gap-1">
            <Badge tone={run.mode === 'apply' ? 'warning' : 'neutral'}>
              {runModeLabel(run.mode)}
            </Badge>
            {/* Snapshot-Verzicht sichtbar in der Liste (Migration
                0029): ein Lauf ohne Rollback-Netz darf in der
                Historie nicht aussehen wie jeder andere. Grund im
                Tooltip, Details im Lauf-Detail. */}
            {run.snapshot_waiver_reason && (
              <span
                title={run.snapshot_waiver_reason}
                data-testid={`run-waiver-${run.id}`}
              >
                <Badge tone="warning">ohne Snapshot</Badge>
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'systeme',
        header: 'Systeme',
        filterValue: (run) => String(run.target_system_ids.length),
        cell: (run) => <span className="text-xs">{run.target_system_ids.length}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        filter: 'select',
        filterValue: (run) => run.failure?.short_label ?? runStatusLabel(run.status),
        cell: (run) => (
          <div
            className="flex flex-col items-start gap-1"
            {...(run.failure
              ? { title: `${run.failure.title} – ${run.failure.action}` }
              : {})}
          >
            {/* Status + Ursache. Ohne die zweite Zeile sehen "gar nicht
                erreicht" und "Wartung gescheitert" identisch aus - genau
                daran ist am 11.08.2026 der Eindruck entstanden, das Tool
                sei kaputt (in Wahrheit fehlte ein Yubikey-Tastendruck). */}
            <RunStatusBadge status={run.status} />
            {run.failure && (
              <Badge
                tone={failureTone(run.failure.group)}
                data-testid={`run-failure-${run.id}`}
              >
                {run.failure.short_label}
                {run.failure.steps_failed > 1 && ` (${run.failure.steps_failed})`}
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'started',
        header: 'Started',
        className: 'text-xs',
        filterValue: (run) => run.started_at ?? run.created_at,
        cell: (run) => <RelativeTime value={run.started_at ?? run.created_at} />,
      },
      {
        id: 'dauer',
        header: 'Dauer',
        className: 'text-xs',
        filterValue: (run) => {
          const label = durationLabel(run);
          return label === '–' ? '' : label;
        },
        cell: (run) => durationLabel(run),
      },
    ];
  }, [customerName, routineNameById]);

  const setKey = <K extends keyof RunsFilter>(key: K, value: RunsFilter[K]): void => {
    const next = { ...filter };
    if (value === undefined || value === '' || value === null) {
      delete next[key];
    } else {
      next[key] = value;
    }
    setFilter(next);
    setCursor(undefined);
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Run-History</h1>
          <p className="text-muted">
            Alle Wartungs-Runs. Live-Update solange Runs aktiv sind.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="size-4" />}
            onClick={() => void runsQuery.refetch()}
            loading={runsQuery.isFetching}
          >
            Aktualisieren
          </Button>
          <Link to="/runs/new">
            <Button leftIcon={<PlayCircle className="size-4" />}>Wartung starten</Button>
          </Link>
        </div>
      </header>

      <Card>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <Combobox
            label="Kunde"
            value={filter.customerId ?? null}
            onChange={(v) => setKey('customerId', v ?? undefined)}
            options={(customersQuery.data?.data ?? []).map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            placeholder="Alle Kunden"
          />
          <Combobox
            label="Routine (Filter clientseitig)"
            value={filter.routineSlug ?? null}
            onChange={(v) => setKey('routineSlug', v ?? undefined)}
            options={(routinesQuery.data?.data ?? []).map((r: Routine) => ({
              value: r.code,
              label: r.display_name,
            }))}
            placeholder="Alle Routinen"
            hint="Backend-Filter folgt - aktuell wird clientseitig gefiltert."
          />
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Status</span>
            <select
              value={filter.status ?? ''}
              onChange={(e) =>
                setKey('status', (e.target.value as RunStatus) || undefined)
              }
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid="runs-filter-status"
            >
              <option value="">Alle</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {runStatusLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Ab Datum</span>
            <input
              type="date"
              value={filter.startedAfter ?? ''}
              onChange={(e) => setKey('startedAfter', e.target.value || undefined)}
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
          </label>
        </div>
      </Card>

      <Card title="Runs">
        {runsQuery.isLoading && <SkeletonRows rows={5} cols={6} />}
        {runsQuery.isError && <ErrorAlert error={runsQuery.error} />}
        {!runsQuery.isLoading && !runsQuery.isError && runs.length === 0 && (
          <EmptyState
            icon={<Activity className="size-8" />}
            title="Noch keine Runs"
            description="Sobald Wartungen laufen, erscheinen sie hier."
            actions={
              <Link to="/runs/new">
                <Button leftIcon={<PlayCircle className="size-4" />}>
                  Erste Wartung starten
                </Button>
              </Link>
            }
          />
        )}
        {runs.length > 0 && (
          <DataTable
            caption="Run-History"
            columns={columns}
            rows={runs}
            rowKey={(run) => run.id}
            storageKey="runs"
            server={pager}
            onRowClick={(run) =>
              void navigate({ to: '/runs/$id', params: { id: run.id } })
            }
            rowTestId={(run) => `run-row-${run.id}`}
          />
        )}
      </Card>
    </div>
  );
}

function durationLabel(run: Run): string {
  if (run.duration_seconds != null) {
    const sec = run.duration_seconds;
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.round(sec / 60)}m`;
    return `${(sec / 3600).toFixed(1)}h`;
  }
  if (isActive(run.status)) return 'läuft …';
  return '–';
}
